import { Button, Col, Form, Modal, Row, Select, Typography, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { StudyProgram, StudentUser } from "@bindeleddet/core/types";
import { useState, useEffect } from "react";
import { IMAGE_ACCEPT, beforeUploadImage, isImageFile } from "helpers/fileValidation";
import type { UploadFile } from "antd/es/upload/interface";

const { Option } = Select;
const { Text, Title } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  studyPrograms: StudyProgram[];
  currentUser: StudentUser;
}

interface ApplicationFormValues {
  study_programme_id: number;
  year_level: number;
  screenshot: File | null;
}

const ChangeStudyProgramModal: React.FC<Props> = ({ open, onClose, studyPrograms, currentUser }) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [allowPdf, setAllowPdf] = useState(false);

  // Sort study programs: filter out "Alle", put indøk (id === 1) first, then alphabetical
  const sortedStudyPrograms = studyPrograms
    .filter(program => program.name !== "Alle")
    .sort((a, b) => {
      // Put indøk (id === 1) first
      if (a.id === 1) return -1;
      if (b.id === 1) return 1;
      // Then sort alphabetically by name
      return a.name.localeCompare(b.name, "no");
    });

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        study_programme_id: currentUser.study_programme,
        year_level: currentUser.year_level,
      });
      setFile(null);
      setFileList([]);
      // Check if backend allows PDF - we'll check this when we create the backend
      // For now, assume only images are allowed
      setAllowPdf(false);
    }
  }, [open, form, currentUser]);

  const handleFileChange = (uploadedFile: File) => {
    // Check if PDF is allowed
    const isPdf = uploadedFile.type === "application/pdf" || uploadedFile.name.toLowerCase().endsWith(".pdf");
    if (isPdf && !allowPdf) {
      message("error", "Ugyldig filtype", "Kun bilder er tillatt");
      return Upload.LIST_IGNORE;
    }
    if (!isPdf && !isImageFile(uploadedFile)) {
      const result = beforeUploadImage(uploadedFile, "Filen må være et bilde (JPG, PNG, GIF eller WebP)");
      if (result === Upload.LIST_IGNORE) return result;
    }
    setFile(uploadedFile);
    setFileList([
      {
        uid: "-1",
        name: uploadedFile.name,
        status: "done",
      },
    ]);
    return false; // Prevent auto upload
  };

  const handleRemove = () => {
    setFile(null);
    setFileList([]);
  };

  const handleSubmit = async (values: ApplicationFormValues) => {
    // Check if at least one field has changed
    if (values.study_programme_id === currentUser.study_programme && values.year_level === currentUser.year_level) {
      message("error", "Ingen endringer", "Du må endre minst linje eller årskull");
      return;
    }

    if (!file) {
      message("error", "Mangler dokumentasjon", "Du må laste opp dokumentasjon");
      return;
    }

    // Validate form
    try {
      await form.validateFields();
    } catch (error) {
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("student_id", String(currentUser.id));
    formData.append("study_programme_id", String(values.study_programme_id));
    formData.append("year_level", String(values.year_level));
    formData.append("past_study_program_id", String(currentUser.study_programme));
    formData.append("past_year_level", String(currentUser.year_level));
    formData.append("screenshot", file);
    formData.append("active", "true");
    formData.append("created_at", new Date().toISOString());

    try {
      await axios.post("/students/study-program-applications/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      message("success", "Søknad sendt", "Din søknad om linjebytte er sendt inn");
      onClose();
    } catch (error) {
      message("error", "Kunne ikke sende søknad", "Det oppstod en feil ved innsending av søknaden");
    } finally {
      setLoading(false);
    }
  };

  const currentStudyProgram = studyPrograms.find(p => p.id === currentUser.study_programme);
  const acceptTypes = allowPdf ? `${IMAGE_ACCEPT},application/pdf` : IMAGE_ACCEPT;

  return (
    <Modal
      title={<Title level={3}>Endre linje eller årskull</Title>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Text strong>Du er registrert med:</Text>
            <br />
            <Text>
              {currentStudyProgram?.name || "Ukjent linje"} - {currentUser.year_level}. klasse
            </Text>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Ny linje"
              name="study_programme_id"
              rules={[{ required: true, message: "Vennligst velg en linje" }]}
            >
              <Select
                placeholder="Velg linje"
                showSearch
                filterOption={(input, option) =>
                  (option?.children?.toString().toLowerCase().indexOf(input.toLowerCase()) ?? -1) >= 0
                }
              >
                {sortedStudyPrograms.map(program => (
                  <Option value={program.id} key={program.id}>
                    {program.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Nytt årskull"
              name="year_level"
              rules={[{ required: true, message: "Vennligst velg årskull" }]}
            >
              <Select placeholder="Velg årskull">
                <Option value={1}>1. klasse</Option>
                <Option value={2}>2. klasse</Option>
                <Option value={3}>3. klasse</Option>
                <Option value={4}>4. klasse</Option>
                <Option value={5}>5. klasse</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Dokumentasjon"
              required
              help={
                allowPdf
                  ? "Last opp bilde eller PDF av bekreftelse av studentstatus fra studentweb"
                  : "Last opp bilde fra studentweb som viser navn og studiestatus"
              }
            >
              <Upload
                accept={acceptTypes}
                beforeUpload={handleFileChange}
                onRemove={handleRemove}
                fileList={fileList}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Last opp fil</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item shouldUpdate>
              {({ getFieldsValue }) => {
                const values = getFieldsValue();
                const hasChanges =
                  (values.study_programme_id !== currentUser.study_programme ||
                    values.year_level !== currentUser.year_level) &&
                  values.study_programme_id &&
                  values.year_level &&
                  file;
                return (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Button block onClick={onClose}>
                        Avbryt
                      </Button>
                    </Col>
                    <Col span={12}>
                      <Button block type="primary" htmlType="submit" loading={loading} disabled={!hasChanges}>
                        Søk
                      </Button>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ChangeStudyProgramModal;
